<script lang="ts" setup>
import { Card } from '@vue-interface/card';
import { InputField } from '@vue-interface/input-field';
import { SelectField } from '@vue-interface/select-field';
import { useForm } from 'laravel-precognition-vue-inertia';
import type { Agency, Instance } from '../types';
import Autocomplete from './Autocomplete.vue';
import FormField from './FormField.vue';
import FormLayout from './FormLayout.vue';
import Skeleton from './Skeleton.vue';

export type DivisionOption = {
  id: string;
  name: string;
};

const props = defineProps<{
  instances?: Instance[];
  instance?: Instance;
  divisions?: DivisionOption[];
  pbtClientId?: string | null;
  url: string;
  agency?: Agency;
}>();

const method = props.agency?.id ? 'put' : 'post';

// Saved credentials are what let the division list load; until then there is
// nothing to choose from.
const hasSavedCredentials = !!props.pbtClientId;

const form = useForm(method, props.url, {
    name: props.agency?.name,
    // master's ordering: the agency's saved instance wins, so the edit form
    // prefills instead of initialising undefined. Only the create page passes
    // the `instance` prop.
    instance_id: props.agency?.instance_id ?? props.instance?.id,
    pbt_client_id: props.pbtClientId ?? null,
    // Write-only: never prefilled, and left blank it keeps the saved secret.
    pbt_client_secret: null as string | null,
    pbt_orgunit_id: props.agency?.pbt_orgunit_id ?? null,
});

function submit() {
    form.submit({
        onSuccess: () => form.reset('pbt_client_secret'),
    });
}
</script>

<template>
    <FormLayout
        :submit="submit"
        :button-label="!agency?.id ? 'Submit' : 'Save'"
        :activity="form.processing"
        :disabled="form.processing">
        <Card>
            <FormField
                label="Name"
                description="The agency, client or group name."
                required>
                <InputField
                    id="name"
                    v-model="form.name"
                    name="name"
                    :error="form.errors.name"
                    @change="form.validate('name')" />
            </FormField>
            <FormField
                label="MessageGears Instance"
                description="The MessageGears instance used to send emails.">
                <Autocomplete
                    id="instance_id"
                    v-model="form.instance_id"
                    name="instance_id"
                    namespace="instances"
                    only="instances"
                    :response="instances"
                    :error="form.errors.instance_id"
                    @update:model-value="form.validate('instance_id')" />
            </FormField>
        </Card>
        <Card>
            <FormField
                label="Powered By Text Client ID"
                :description="agency?.pbt_account_id
                    ? `Connected to Powered By Text account ${agency.pbt_account_id}.`
                    : 'The API client ID of the agency\'s Powered By Text sub-account.'">
                <InputField
                    id="pbt_client_id"
                    v-model="form.pbt_client_id"
                    name="pbt_client_id"
                    autocomplete="off"
                    :error="form.errors.pbt_client_id" />
            </FormField>
            <FormField
                label="Powered By Text Client Secret"
                :description="hasSavedCredentials
                    ? 'A secret is saved. Leave blank to keep it.'
                    : 'The sub-account\'s API client secret.'">
                <InputField
                    id="pbt_client_secret"
                    v-model="form.pbt_client_secret"
                    type="password"
                    name="pbt_client_secret"
                    autocomplete="new-password"
                    :error="form.errors.pbt_client_secret" />
            </FormField>
            <FormField
                v-if="agency?.id"
                label="Division"
                description="The Powered By Text division used to send text campaigns.">
                <p
                    v-if="!hasSavedCredentials"
                    class="text-sm text-neutral-500 dark:text-neutral-400">
                    Save the client ID and secret first, then choose a division.
                </p>
                <Skeleton
                    v-else-if="divisions === undefined"
                    class="h-9 rounded" />
                <SelectField
                    v-else
                    id="pbt_orgunit_id"
                    v-model="form.pbt_orgunit_id"
                    name="pbt_orgunit_id"
                    :error="form.errors.pbt_orgunit_id">
                    <option :value="null">
                        No division
                    </option>
                    <option
                        v-for="division in divisions"
                        :key="division.id"
                        :value="division.id">
                        {{ division.name }}
                    </option>
                </SelectField>
            </FormField>
        </Card>
    </FormLayout>
</template>
